import { formatDistanceToNow } from 'date-fns';
import { Check, Star, Users } from 'lucide-react';
import { ClassificationBadge } from '@/components/custom/classification-badge';
import type { TaskWithRelations } from '@vagusx/clients';
import type { ClassificationLabelMap } from '@/hooks/use-classification-label';
import { cn } from '@/lib/utils';

interface TaskListItemProps {
  task: TaskWithRelations;
  isSelected: boolean;
  onClick: () => void;
  onToggleStar?: (id: string, isStarred: boolean) => void;
  labelMap: ClassificationLabelMap;
  /**
   * Bulk-select state. When `bulkChecked` is true (or when ANY row on
   * the page is bulk-checked, signalled by `selectionMode`), the
   * leftmost checkbox is rendered fully visible. Otherwise it
   * fades in only on row hover. Click receives the MouseEvent so
   * the parent can branch on `event.shiftKey` for range select.
   * Both props optional — pages that don't opt into bulk-select
   * keep the original layout exactly.
   */
  bulkChecked?: boolean;
  selectionMode?: boolean;
  onBulkCheckboxClick?: (event: React.MouseEvent) => void;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0]!.charAt(0)}${parts[parts.length - 1]!.charAt(0)}`.toUpperCase();
  }
  return name.charAt(0).toUpperCase();
}

/**
 * Decide which leftmost-slot element is rendered as ALWAYS visible
 * (vs hover-revealed). Both elements are absolutely-positioned
 * inside a 16x16 cell; CSS `group-hover` handles the hover-reveal
 * for the checkbox transparently. This helper just picks the
 * default-state element when nothing is hovered:
 *
 *   - bulkChecked → checkbox (this row is selected; always show it)
 *   - selectionMode → checkbox (any row on page is selected; show
 *     all of them so the user can extend the selection by clicking)
 *   - otherwise → dot (default state, hover reveals checkbox)
 */
export function leftSlotDefault(opts: {
  selectionMode: boolean;
  bulkChecked: boolean;
}): 'dot' | 'checkbox' {
  if (opts.bulkChecked || opts.selectionMode) return 'checkbox';
  return 'dot';
}

export function TaskListItem({
  task,
  isSelected,
  onClick,
  onToggleStar,
  labelMap,
  bulkChecked,
  selectionMode,
  onBulkCheckboxClick,
}: TaskListItemProps): React.ReactElement {
  const showCheckbox = onBulkCheckboxClick !== undefined;
  const isUnread = !task.readAt;
  const isStarred = !!task.starredAt;
  const timeAgo = formatDistanceToNow(new Date(task.createdAt));
  const defaultSlot = leftSlotDefault({
    selectionMode: !!selectionMode,
    bulkChecked: !!bulkChecked,
  });

  // Derive classification badges sorted by label sortOrder for consistent display
  const classificationLabels = getClassificationLabels(task, labelMap);

  return (
    <button
      type="button"
      data-task-id={task.id}
      className={cn(
        'group/task w-full border-l-2 px-3 py-2.5 text-left transition-colors',
        isSelected ? 'border-l-primary bg-accent' : 'border-l-transparent hover:bg-accent/50',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        {/* Leftmost slot — shared between the unread dot (default) and
            the bulk-select checkbox (hover / selectionMode / checked).
            One 16x16 cell holds both, so the layout stays constant
            regardless of bulk-select state. Without this swap, the
            checkbox column ate ~24px of every row's content even when
            the user wasn't selecting anything.

            Pages that don't opt into bulk-select (no
            `onBulkCheckboxClick` prop) get the original 8px dot. */}
        {showCheckbox ? (
          <span className="relative mt-1 h-4 w-4 shrink-0">
            {/* Unread dot — visible only when the checkbox is hidden. */}
            <span
              aria-hidden
              className={cn(
                'absolute inset-0 m-auto h-2 w-2 rounded-full',
                isUnread ? 'bg-primary' : 'bg-transparent',
                defaultSlot === 'checkbox' ? 'opacity-0' : 'group-hover/task:opacity-0'
              )}
            />
            {/* Checkbox — hidden by default; visible on hover, in
                selectionMode, or when this row is checked. Click stops
                propagation so the row's onClick (which navigates to
                detail) doesn't fire. Rendered as a span (not a real
                <input>) because the row is a <button> and
                input-in-button is invalid HTML. */}
            <span
              role="checkbox"
              aria-checked={bulkChecked ?? false}
              aria-label={bulkChecked ? 'Unselect task' : 'Select task'}
              tabIndex={-1}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBulkCheckboxClick?.(e);
              }}
              className={cn(
                'absolute inset-0 inline-flex cursor-pointer items-center justify-center rounded border',
                bulkChecked
                  ? 'border-primary bg-primary text-primary-foreground opacity-100'
                  : defaultSlot === 'checkbox'
                    ? 'border-input bg-background opacity-100'
                    : 'border-input bg-background opacity-0 group-hover/task:opacity-100'
              )}
            >
              {bulkChecked && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
          </span>
        ) : (
          // Pages without bulk-select: original 8px dot.
          <span
            className={cn(
              'mt-1.5 h-2 w-2 shrink-0 rounded-full',
              isUnread ? 'bg-primary' : 'bg-transparent'
            )}
          />
        )}
        <div className="min-w-0 flex-1">
          {/* Title + time */}
          <div className="flex items-center gap-1">
            <span
              className={cn(
                'min-w-0 shrink truncate text-sm',
                isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
              )}
            >
              {task.title}
            </span>
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">{timeAgo}</span>
          </div>

          {/* Body (hidden when identical to title) */}
          {task.body && task.body !== task.title && (
            <p className="truncate text-xs text-muted-foreground">{task.body}</p>
          )}

          {/* Labels row */}
          <div className="mt-0.5 flex items-center gap-1.5 group-hover/task:flex-wrap">
            {onToggleStar && (
              <span
                role="button"
                tabIndex={0}
                className="shrink-0 cursor-pointer focus-visible:outline-none"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleStar(task.id, isStarred);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggleStar(task.id, isStarred);
                  }
                }}
                title={isStarred ? 'Unstar' : 'Star'}
              >
                <Star
                  className={cn(
                    'h-3.5 w-3.5',
                    isStarred
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-muted-foreground/40 hover:text-yellow-400'
                  )}
                />
              </span>
            )}
            {classificationLabels.slice(0, 3).map((label) => (
              <ClassificationBadge
                key={label}
                classification={label}
                labelMap={labelMap}
                size="sm"
              />
            ))}
            {classificationLabels.length > 3 && (
              <span className="text-[10px] text-muted-foreground group-hover/task:hidden">
                +{classificationLabels.length - 3}
              </span>
            )}
            {classificationLabels.slice(3).map((label) => (
              <span key={label} className="hidden group-hover/task:inline-flex">
                <ClassificationBadge classification={label} labelMap={labelMap} size="sm" />
              </span>
            ))}
            {task.assignedToName &&
              (task.assignedToType === 'team' ? (
                <span
                  className="ml-auto inline-flex h-4 shrink-0 items-center gap-0.5 rounded-full bg-muted px-1.5 text-[9px] font-medium text-muted-foreground"
                  title={`Team: ${task.assignedToName}`}
                >
                  <Users className="h-2.5 w-2.5" />
                  <span className="max-w-16 truncate">{task.assignedToName}</span>
                </span>
              ) : (
                <span
                  className="ml-auto inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-medium text-muted-foreground"
                  title={task.assignedToName}
                >
                  {getInitials(task.assignedToName)}
                </span>
              ))}
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * Get labels for display, sorted by sortOrder from the label map.
 */
function getClassificationLabels(
  task: TaskWithRelations,
  labelMap: ClassificationLabelMap
): string[] {
  if (task.labels && task.labels.length > 0) {
    return [...task.labels].sort(
      (a, b) => (labelMap.get(a)?.sortOrder ?? 999) - (labelMap.get(b)?.sortOrder ?? 999)
    );
  }
  return [];
}
